import { useCallback } from 'react';
import { ZodSchema, ZodError } from 'zod';

interface ValidationResult<T> {
  success: boolean;
  data?: T;
  errors: Record<string, string>;
}

/**
 * Hook for validating form data against Zod schemas
 * Returns both validation status and formatted error messages
 */
export function useFormValidation() {
  const validateForm = useCallback(
    <T,>(schema: ZodSchema, data: unknown): ValidationResult<T> => {
      try {
        const validatedData = schema.parse(data);
        return {
          success: true,
          data: validatedData as T,
          errors: {},
        };
      } catch (error) {
        if (error instanceof ZodError) {
          const errors: Record<string, string> = {};

          // Convert zod errors to field-indexed error messages
          error.issues.forEach((err) => {
            const fieldPath = err.path.join('.');
            
            // Map api field names (snake_case) to form field names (camelCase)
            const fieldName = fieldPath
              .split('_')
              .map((part, i) => i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1))
              .join('');

            errors[fieldName] = err.message;
            // Also store with snake_case for API field names
            errors[fieldPath] = err.message;
          });

          return {
            success: false,
            errors,
          };
        }

        return {
          success: false,
          errors: { _global: 'An unexpected validation error occurred' },
        };
      }
    },
    []
  );

  return { validateForm };
}
